import { CellContext, ColumnDef, RowData } from "@tanstack/react-table";
import {
  ExploreColumnType,
  exploreColumnTypeToLabel,
  ExploreAggColumnType,
  exploreAggColumnTypeToLabel,
  exploreGroupByColumnTypeToLabel,
  isExploreGroupByColumnType,
  ExploreGroupByColumnType,
} from "./ExploreColum.model";
import {
  ExploreInteractionRow,
  ExploreAggInteractionRow,
  ExploreInteractionType,
  ExploreEnrichment,
  exploreInteractionTypeToLabel,
  ExploreCustomField,
} from "../Explore.model";
import {
  displayIdCell,
  collaboratorsCell,
  teamsCell,
  ticketIdCell,
  escalationReasonCell,
  validationStatusCell,
  timeToResolutionCell,
  timeToFirstResponseCell,
  resolutionStatusCell,
  skipReasonCell,
  resolvedReasonCell,
  groupByResolutionStatusCell,
  groupByEscalationReasonCell,
  groupBySkipReasonCell,
  groupByResolvedReasonCell,
  originAuthorCell,
  isQuackAnsweredCell,
  groupByIsQuackAnsweredCell,
  isAnsweredByAuiCell,
  groupByIsAnsweredByAuiCell,
  avgSentimentCell,
  skillsUsedCell,
} from "./explore-columns";
import {
  dateTimeCell,
  titleCell,
  numberCell,
  statusCell,
  channelCell,
  topicCell,
  categoryCell,
  avgQaScoreCell,
  groupByTitleCell,
  placeholderCell,
  groupByCategoryCell,
  groupByAvgQaScoreRoundRangeCell,
  groupByAvgQuackMessageScoreRoundRangeCell,
  csatCell,
  ratioCell,
  defaultCellRenderer,
  booleanCell,
} from "@/v2/components/table/cellRenderers";
import { CSATFormatter } from "@/v2/components/formatters/CSATFormatter";
import { useMemo } from "react";
import { Badge, Em, Flex, Text } from "@radix-ui/themes";

type Helper<RowDataType extends RowData> = {
  cell: (props: CellContext<RowDataType, unknown>) => JSX.Element;
  align?: "left" | "right";
  size?: number;
  minSize?: number;
  headerTooltip?: string | React.ReactNode;
  cellTooltip?: boolean;
};

const exploreInteractionColumns: Record<
  ExploreColumnType,
  Helper<ExploreInteractionRow> | null
> = {
  tenantId: null,
  id: null,
  displayId: {
    cell: displayIdCell,
    align: "right",
    size: 100,
  },
  subject: {
    cell: titleCell,
    size: 400,
  },
  description: null,
  createdAt: {
    cell: dateTimeCell,
  },
  updatedAt: {
    cell: dateTimeCell,
  },
  status: {
    cell: statusCell,
    size: 240,
  },
  channel: {
    cell: channelCell,
    size: 200,
  },
  topic: {
    cell: topicCell,
    size: 400,
    headerTooltip: (
      <Flex gap="2" direction="column">
        <Text size="2">
          ✨ Quack AI generates this topic automatically to help <br />
          classify and organize your conversations.
        </Text>
        <Badge size="1" variant="soft" color="yellow" className=" w-fit">
          <Em>Auto Generated by Quack</Em>
        </Badge>
      </Flex>
    ),
  },
  subTopic: {
    cell: topicCell,
    size: 400,
    headerTooltip: (
      <Flex gap="2" direction="column">
        <Text size="2">
          ✨ Quack analyzes every interaction, cleans the data, and highlights{" "}
          <br />
          the user’s main intent—helping you uncover the true voice of your
          <br />
          customers.
        </Text>
        <Badge size="1" variant="soft" color="yellow" className=" w-fit">
          <Em>Auto Generated by Quack</Em>
        </Badge>
      </Flex>
    ),
  },
  category: {
    cell: categoryCell,
    size: 400,
    headerTooltip: (
      <Flex gap="2" direction="column">
        <Text size="2">
          The type of interaction, using a curated set tailored for
          <br />
          product and software companies.
        </Text>
        <Badge size="1" variant="soft" color="yellow" className=" w-fit">
          <Em>Auto Generated by Quack</Em>
        </Badge>
      </Flex>
    ),
  },
  agentAssigned: null,
  agentAssignedId: null,
  team: null,
  teams: {
    cell: teamsCell,
    cellTooltip: false,
  },
  tags: null,
  tenantUser: null,
  tenantOrganization: null,
  avgQaScore: {
    cell: (cellContext) =>
      avgQaScoreCell({
        ...cellContext,
        scorecards: cellContext.row.original.scorecards,
      }),
    headerTooltip: (
      <Flex gap="2" direction="column">
        <Text size="2">
          ✨ Calculated using your custom scorecard. <br />
          If none is set, the default Quack scorecard is used.
        </Text>
        <Badge size="1" variant="soft" color="yellow" className=" w-fit">
          <Em>Auto Generated by Quack with Adjustments of Agents</Em>
        </Badge>
      </Flex>
    ),
    cellTooltip: false,
  },
  avgAutoQaScore: {
    cell: (cellContext) =>
      avgQaScoreCell({
        ...cellContext,
        scorecards: cellContext.row.original.scorecards,
      }),
    headerTooltip: (
      <Flex gap="2" direction="column">
        <Text size="2">
          ✨ Calculated using your custom scorecard. <br />
          If none is set, the default Quack scorecard is used.
        </Text>
        <Badge size="1" variant="soft" color="yellow" className=" w-fit">
          <Em>Auto Generated by Quack</Em>
        </Badge>
      </Flex>
    ),
    cellTooltip: false,
  },
  avgQaScoreRoundRange: null,
  avgQuackMessageScoreRoundRange: null,
  avgSentiment: {
    cell: avgSentimentCell,
    align: "right",
    cellTooltip: false,
    headerTooltip: (
      <Flex gap="2" direction="column">
        <Text size="2">
          ✨ Shows the average emotional tone from all customer messages.
        </Text>
        <Badge size="1" variant="soft" color="yellow" className=" w-fit">
          <Em>Auto Generated by Quack</Em>
        </Badge>
      </Flex>
    ),
  },
  lastSentiment: {
    cell: avgSentimentCell,
    align: "right",
    cellTooltip: false,
    headerTooltip: (
      <Flex gap="2" direction="column">
        <Text size="2">
          ✨ Shows the last emotional tone from the customer messages.
        </Text>
        <Badge size="1" variant="soft" color="yellow" className=" w-fit">
          <Em>Auto Generated by Quack</Em>
        </Badge>
      </Flex>
    ),
  },
  avgQuackMessageScore: {
    cell: avgQaScoreCell,
    align: "right",
    cellTooltip: false,
    headerTooltip: (
      <Text size="2">
        Average of the scores customers gave to the quack agent messages in this
        conversation.
      </Text>
    ),
  },
  csat: {
    cell: csatCell,
    align: "right",
  },
  csatComment: null,
  countPublicMessages: {
    cell: numberCell,
    align: "right",
    headerTooltip: "# of messages sent by the user and the agent.",
  },
  countInternalMessages: {
    cell: numberCell,
    align: "right",
    headerTooltip: "# of messages that was sent internally ",
  },
  timeToResolution: {
    cell: timeToResolutionCell,
    size: 100,
    align: "right",
  },
  timeToFirstResponse: {
    cell: timeToFirstResponseCell,
    align: "right",
  },
  countQuackMessages: {
    cell: numberCell,
    align: "right",
  },
  isQuackEscalated: null,
  isQuackAnswered: {
    cell: isQuackAnsweredCell,
    size: 140,
  },
  isAnsweredByAui: {
    cell: isAnsweredByAuiCell,
    size: 140,
  },
  collaborators: {
    cell: collaboratorsCell,
    size: 400,
    cellTooltip: false,
  },
  isFiniteState: null,
  validationStatus: {
    cell: validationStatusCell,
  },
  ticketId: {
    cell: ticketIdCell,
    cellTooltip: false,
    // size: 200,
  },
  resolutionStatus: {
    cell: resolutionStatusCell,
    size: 240,
  },
  escalationReason: {
    cell: escalationReasonCell,
    size: 240,
  },
  skipReason: {
    cell: skipReasonCell,
    size: 240,
  },
  resolvedReason: {
    cell: resolvedReasonCell,
    size: 240,
  },
  originAuthor: {
    cell: originAuthorCell,
    size: 240,
  },
  originUrl: {
    cell: defaultCellRenderer,
    size: 400,
    headerTooltip: "The URL of the origin of the interaction.",
  },
  isUserAuthenticated: {
    cell: booleanCell,
    size: 140,
  },
  scoreTitle: null,
  scoreContent: null,
  simulatorId: null,
  skillUsed: null,
  skillsUsed: {
    cell: skillsUsedCell,
    size: 400,
    cellTooltip: false,
  },
  avgCsat: null,
};

const exploreAggInteractionColumns: Record<
  ExploreAggColumnType,
  Helper<ExploreAggInteractionRow> | null
> = {
  volume: {
    cell: numberCell,
    align: "right",
  },
  volumeRatio: {
    cell: ratioCell,
  },
  avgQuackMessages: {
    cell: numberCell,
    align: "right",
  },
  avgQaScore: {
    cell: avgQaScoreCell,
    headerTooltip: (
      <Flex gap="2" direction="column">
        <Text size="2">
          ✨ Calculated using your custom scorecard. <br />
          If none is set, the default Quack scorecard is used.
        </Text>
        <Badge size="1" variant="soft" color="yellow" className=" w-fit">
          Auto Generated by Quack
        </Badge>
      </Flex>
    ),
  },
  avgAutoQaScore: {
    cell: avgQaScoreCell,
  },
  avgSentiment: {
    cell: avgSentimentCell,
    // align: "right",
  },
  avgCsat: {
    cell: csatCell,
    align: "right",
  },
  avgTimeToResolution: {
    cell: timeToResolutionCell,
    align: "right",
  },
  avgTimeToFirstResponse: {
    cell: timeToFirstResponseCell,
    align: "right",
  },
  avgPublicMessages: {
    cell: numberCell,
    align: "right",
  },
  avgInternalMessages: {
    cell: numberCell,
    align: "right",
  },
  countAgentsAssigned: {
    cell: numberCell,
    align: "right",
  },
  avgQaScoreByTitle: {
    cell: numberCell,
    align: "right",
  },
  avgQaScoreByContent: {
    cell: numberCell,
    align: "right",
  },
};

const exploreGroupByColumns: Record<
  ExploreGroupByColumnType,
  Helper<ExploreAggInteractionRow> | null
> = {
  agentAssigned: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByTitleCell,
        placeholder: "Unknown Agent",
      }),
    size: 400,
  },
  day: null,
  week: null,
  month: null,
  tenantId: null,
  team: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByTitleCell,
        placeholder: "Unknown Team",
      }),
    size: 400,
  },
  status: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByTitleCell,
      }),
    size: 400,
  },
  channel: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByTitleCell,
      }),
    size: 400,
  },
  topic: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByTitleCell,
      }),
    size: 400,
  },
  subTopic: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByTitleCell,
      }),
    size: 400,
  },
  category: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByCategoryCell,
        placeholder: "Uncategorized",
      }),
    size: 400,
  },
  avgQaScore: null,
  avgQaScoreRoundRange: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        placeholder: "Not Scored",
        cellRender: (cellContext) =>
          groupByAvgQaScoreRoundRangeCell(cellContext),
      }),
    cellTooltip: false,
  },
  avgQuackMessageScoreRoundRange: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        placeholder: "No Score",
        cellRender: (cellContext) =>
          groupByAvgQuackMessageScoreRoundRangeCell(cellContext),
      }),
    cellTooltip: false,
  },
  avgSentiment: null,
  isQuackAnswered: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByIsQuackAnsweredCell,
        placeholder: "Unknown",
      }),
    size: 140,
  },
  isAnsweredByAui: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByIsAnsweredByAuiCell,
        placeholder: "Unknown",
      }),
    size: 140,
  },
  resolutionStatus: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByResolutionStatusCell,
        placeholder: "Unclassified",
      }),
    size: 400,
  },
  escalationReason: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByEscalationReasonCell,
        placeholder: "Unknown Escalation Reason",
      }),
    size: 400,
  },
  skipReason: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupBySkipReasonCell,
        placeholder: "Unknown Skip Reason",
      }),
    size: 400,
  },
  resolvedReason: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByResolvedReasonCell,
        placeholder: "Unknown Resolved Reason",
      }),
    size: 400,
  },
  originUrl: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        cellRender: groupByTitleCell,
        placeholder: "Unknown Origin URL",
      }),
    size: 400,
  },
  scoreTitle: null,
  scoreContent: null,
  simulator: null,
  skillUsed: null,
  avgCsat: {
    cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
      placeholderCell({
        args,
        placeholder: "Not Rated",
        cellRender: (cellContext) => {
          const value = cellContext.getValue();
          return value != null ? (
            <CSATFormatter value={Number(value)} />
          ) : (
            <Em>Not Rated</Em>
          );
        },
      }),
    size: 120,
  },
};

export const visibleConversationColumns: ExploreColumnType[] = [
  "subject",
  "displayId",
  "createdAt",
  "updatedAt",
  "status",
  "channel",
  "description",
  "resolutionStatus",
  "escalationReason",
  "skipReason",
  "resolvedReason",
  "topic",
  "subTopic",
  "category",
  "tenantUser",
  "isQuackAnswered",
  "isAnsweredByAui",
  "avgQaScore",
  "avgAutoQaScore",
  "avgSentiment",
  "lastSentiment",
  "avgQuackMessageScore",
  "csat",
  "csatComment",
  "timeToResolution",
  "countPublicMessages",
  "countQuackMessages",
  "ticketId",
  "originUrl",
  "simulatorId",
  "skillsUsed",
  "isUserAuthenticated",
];

export const visibleTicketColumns: ExploreColumnType[] = [
  "subject",
  "displayId",
  "createdAt",
  "updatedAt",
  "status",
  "channel",
  "topic",
  "subTopic",
  "description",
  "category",
  "agentAssigned",
  "teams",
  "tenantUser",
  "tenantOrganization",
  "avgQaScore",
  "avgAutoQaScore",
  "avgSentiment",
  "lastSentiment",
  "csat",
  "csatComment",
  "countPublicMessages",
  "countInternalMessages",
  "timeToResolution",
  "timeToFirstResponse",
  "resolutionStatus",
  "escalationReason",
  "skipReason",
  "countQuackMessages",
  "isQuackAnswered",
  "isAnsweredByAui",
  "isQuackEscalated",
  "collaborators",
  "validationStatus",
  "originAuthor",
  "simulatorId",
  "skillsUsed",
];

export const visibleTicketAggColumns: ExploreAggColumnType[] = [
  "volume",
  "volumeRatio",
  "avgQuackMessages",
  "avgQaScore",
  "avgSentiment",
  "avgCsat",
  "avgTimeToResolution",
  "avgTimeToFirstResponse",
  "avgPublicMessages",
  "avgInternalMessages",
  "countAgentsAssigned",
  "avgQaScoreByTitle",
  "avgQaScoreByContent",
];

export const visibleConversationAggColumns: ExploreAggColumnType[] = [
  "volume",
  "volumeRatio",
  "avgQuackMessages",
  "avgQaScore",
  "avgSentiment",
  "avgCsat",
  "avgTimeToResolution",
  "avgTimeToFirstResponse",
  "avgPublicMessages",
  "avgInternalMessages",
  "countAgentsAssigned",
];

export function useExploreColumns({
  source,
  groupBy,
  visibleColumns = source === "tickets"
    ? visibleTicketColumns
    : visibleConversationColumns,
  visibleAggColumns = source === "tickets"
    ? visibleTicketAggColumns
    : visibleConversationAggColumns,
  enrichments,
  customFields,
}: {
  source: ExploreInteractionType;
  visibleColumns?: ExploreColumnType[];
  visibleAggColumns?: ExploreAggColumnType[];
  groupBy?: string;
  enrichments?: ExploreEnrichment[];
  customFields?: ExploreCustomField[];
}) {
  const columns = useMemo<ColumnDef<ExploreInteractionRow>[]>(() => {
    const columns: ColumnDef<ExploreInteractionRow>[] = [];
    const addedIds = new Set<string>();

    visibleColumns.forEach((key) => {
      if (addedIds.has(key)) {
        return;
      }
      addedIds.add(key);

      const helper = exploreInteractionColumns[key];

      const header =
        key === "subject"
          ? exploreInteractionTypeToLabel[source].singular
          : exploreColumnTypeToLabel[key];

      if (helper != null) {
        columns.push({
          id: key,
          accessorKey: key,
          cell: helper.cell,
          header,
          meta: {
            align: helper.align,
            headerTooltip: helper.headerTooltip,
            cellTooltip: helper.cellTooltip,
          },
          size: helper.size,
          minSize: helper.minSize,
        });
      } else {
        columns.push({
          id: key,
          accessorKey: key,
          header,
        });
      }
    });

    customFields?.forEach((customField) => {
      if (addedIds.has(customField.id)) {
        return;
      }
      addedIds.add(customField.id);
      columns.push({
        id: customField.id,
        accessorKey: customField.label,
        header: customField.label,
      });
    });

    return columns;
  }, [visibleColumns, source, customFields]);

  const groupByColumn = useMemo(() => {
    if (groupBy == null) {
      return null;
    }

    const enrichmentGroupBy = enrichments?.find(
      (enrichment) => enrichment.value === groupBy,
    );

    const customFieldGroupBy = customFields?.find(
      (customField) => customField.value === groupBy,
    );

    if (enrichmentGroupBy) {
      return {
        id: enrichmentGroupBy.value,
        accessorKey: enrichmentGroupBy.value,
        header: enrichmentGroupBy.label,
        cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
          placeholderCell({
            args,
            cellRender: groupByTitleCell,
          }),
        size: 400,
      };
    }

    if (customFieldGroupBy) {
      return {
        id: customFieldGroupBy.id,
        accessorKey: customFieldGroupBy.value,
        header: customFieldGroupBy.label,
        cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
          placeholderCell({
            args,
            cellRender: groupByTitleCell,
          }),
        size: 400,
      };
    }

    const header =
      groupBy === "customFields"
        ? "Custom fields"
        : isExploreGroupByColumnType(groupBy)
          ? exploreGroupByColumnTypeToLabel[groupBy]?.singular
          : groupBy;

    if (isExploreGroupByColumnType(groupBy)) {
      const helper = exploreGroupByColumns[groupBy];

      if (helper != null) {
        return {
          id: groupBy,
          accessorKey: groupBy,
          header,
          cell: helper.cell,
          meta: {
            align: helper.align,
            headerTooltip: helper.headerTooltip,
            cellTooltip: helper.cellTooltip,
          },
          size: helper.size,
          minSize: helper.minSize,
        };
      }

      return {
        id: groupBy,
        accessorKey: groupBy,
        header,
        cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
          placeholderCell({
            args,
            cellRender: groupByTitleCell,
          }),
        size: 400,
      };
    }

    return {
      id: groupBy,
      accessorKey: groupBy,
      header,
      cell: (args: CellContext<ExploreAggInteractionRow, unknown>) =>
        placeholderCell({
          args,
          cellRender: groupByTitleCell,
        }),
      size: 400,
    };
  }, [groupBy, enrichments, customFields]);

  const aggColumns = useMemo<ColumnDef<ExploreAggInteractionRow>[]>(() => {
    const aggColumns: ColumnDef<ExploreAggInteractionRow>[] = [];

    visibleAggColumns.forEach((key) => {
      const helper = exploreAggInteractionColumns[key];

      const header = exploreAggColumnTypeToLabel[key];

      if (helper != null) {
        aggColumns.push({
          id: key,
          accessorKey: key,
          cell: helper.cell,
          header,
          meta: {
            align: helper.align,
            headerTooltip: helper.headerTooltip,
            cellTooltip: helper.cellTooltip,
          },
          size: helper.size,
          minSize: helper.minSize,
        });
      } else {
        aggColumns.push({
          id: key,
          accessorKey: key,
          header,
        });
      }
    });

    if (groupByColumn != null) {
      if (groupBy === "avgQaScore" || groupBy === "avgQaScoreRoundRange") {
        return [
          groupByColumn,
          ...aggColumns.filter((column) => column.id !== "avgQaScore"),
        ];
      }

      return [groupByColumn, ...aggColumns];
    }

    return aggColumns;
  }, [visibleAggColumns, groupByColumn, groupBy]);

  return { columns, aggColumns };
}
